import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { createSlice } from "@reduxjs/toolkit";
import { getStore } from "../../storeRef";
import { handleApiError } from "Helper";

//--------------------------------------------------------------------------------------
//  For redux service for applciation scoped (data wont change in application lifetime)
//--------------------------------------------------------------------------------------
export function createApplicationService({
  name,
  apiFetchFn,
  apiAddFn,
  apiUpdateFn,
}) {
  const slice = createSlice({
    name,
    initialState: {
      data: [],
      fetched: false,
    },
    reducers: {
      setData(state, action) {
        state.data = action.payload;
        state.fetched = true;
      },
      resetFetched(state) {
        state.fetched = false;
      },
    },
  });

  const { setData } = slice.actions;
  const reducer = slice.reducer;

  const _fetchData = () => async (dispatch) => {
    try {
      const result = await apiFetchFn();
      dispatch(setData(result.data.data));
      return result.data.data;
    } catch (error) {
      handleApiError(error);
    }
  };
  let inProgress = false;

  const getData = async () => {
    const store = getStore();
    const state = store.getState();
    if (!state[name].fetched) {
      if (!inProgress) {
        inProgress = true;
        try {
          await store.dispatch(_fetchData());
        } finally {
          inProgress = false;
        }
      } else {
        await new Promise((res) => setTimeout(res, 100));
      }
    }
    return getStore().getState()[name].data;
  };

  const addData = async (data) => {
    try {
      await apiAddFn(data);
      getStore().dispatch(_fetchData());
      return { success: true };
    } catch (error) {
      handleApiError(error);
      return { success: false, error };
    }
  };

  const updateData = async (id, data) => {
    try {
      await apiUpdateFn(id, data);
      getStore().dispatch(_fetchData());
      return { success: true };
    } catch (error) {
      handleApiError(error);
      return { success: false, error };
    }
  };

  const useData = () => {
    const data = useSelector((state) => state[name].data);
    const fetched = useSelector((state) => state[name].fetched);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
      if (!fetched) {
        setLoading(true);
        getData().finally(() => setLoading(false));
      }
    }, [fetched]);
    return { data, loading };
  };

  const resetData = () => {
    getStore().dispatch(slice.actions.resetFetched());
  };

  return {
    reducer,
    getData,
    addData,
    updateData,
    useData,
    resetData,
  };
}
